import React, { useState, useEffect } from 'react';
import { useQuery } from '@tanstack/react-query';
import { SearchableSelect, SearchableSelectOption } from './SearchableSelect';
import { getCountries, getCountriesRu, CountryInfo } from '../services/countryService';
import { useTranslation } from '../hooks/useTranslation';
import { Language } from '../types/language';

interface CountrySelectProps {
  value: string;
  onChange: (value: string) => void;
  placeholder?: string;
  className?: string;
  required?: boolean;
  error?: string;
  disabled?: boolean;
  language?: Language; // Опциональный язык, если не указан, определяется из контекста
}

export const CountrySelect: React.FC<CountrySelectProps> = ({
  value,
  onChange,
  placeholder,
  className = '',
  required = false,
  error,
  disabled = false,
  language
}) => {
  const { t, language: currentLanguage } = useTranslation();
  const [options, setOptions] = useState<SearchableSelectOption[]>([]);
  
  // Определяем язык для запроса стран
  const targetLanguage = language || currentLanguage;
  
  // Определяем, какой endpoint использовать
  const useRuEndpoint = targetLanguage === Language.RU;
  
  // Загружаем список стран
  const { data: countries, isLoading } = useQuery<CountryInfo[]>({
    queryKey: ['countries', useRuEndpoint ? 'ru' : targetLanguage],
    queryFn: () => useRuEndpoint ? getCountriesRu() : getCountries(targetLanguage),
    staleTime: 1000 * 60 * 60, // Кэшируем на 1 час
    gcTime: 1000 * 60 * 60 * 24, // Храним в кэше 24 часа (gcTime заменил cacheTime в React Query v5)
  });

  // Преобразуем список стран в опции для SearchableSelect
  useEffect(() => {
    if (countries && countries.length > 0) {
      const countryOptions: SearchableSelectOption[] = countries.map((country: CountryInfo) => ({
        value: country.code,
        label: country.name,
        searchText: `${country.code} ${country.name}` // Поиск по коду и названию
      }));
      setOptions(countryOptions);
    } else {
      setOptions([]);
    }
  }, [countries]);

  const defaultPlaceholder = placeholder || t('counterparty.countryPlaceholder') || 'Выберите страну...';

  if (isLoading) {
    return (
      <div className={`w-full px-3 py-2 border border-gray-300 rounded-lg bg-gray-50 ${className}`}>
        <div className="text-sm text-gray-500">{t('common.loading') || 'Загрузка...'}</div>
      </div>
    );
  }

  return (
    <SearchableSelect
      options={options}
      value={value}
      onChange={onChange}
      placeholder={defaultPlaceholder}
      className={className}
      required={required}
      error={error}
      disabled={disabled}
      noResultsText={t('common.noResults') || 'Страна не найдена'}
    />
  );
};
